'use client'

import type { MouseEvent } from 'react'
import type {
	ButtonProps,
	SRT_Row,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import { ChevronRightIcon } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { cn } from '../../lib/utils'
import { parseFromValuesOrFunc } from '../../utils/utils'

export type SRT_ExpandButtonProps<TData extends SRT_RowData> = {
	row: SRT_Row<TData>
	staticRowIndex?: number
	table: SRT_TableInstance<TData>
} & ButtonProps

export const SRT_ExpandButton = <TData extends SRT_RowData>({
	row,
	staticRowIndex,
	table,
	...props
}: SRT_ExpandButtonProps<TData>) => {
	const {
		//getState,
		options: {
			localization,
			shadcnExpandButtonProps,
			positionExpandColumn,
			renderDetailPanel,
		},
	} = table
	//const { density } = getState()

	const buttonProps = parseFromValuesOrFunc(shadcnExpandButtonProps, {
		row,
		staticRowIndex,
		table,
	})

	const canExpand = row.getCanExpand()
	if (!canExpand) {
		return null
	}
	const isExpanded = row.getIsExpanded()

	const handleToggleExpand = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation()
		row.toggleExpanded()
		buttonProps?.onClick?.(event)
	}

	const detailPanel = !!renderDetailPanel?.({ row, table })

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					variant='ghost'
					size='icon-sm'
					aria-label={localization.expand}
					disabled={!canExpand && !detailPanel}
					{...buttonProps}
					{...props}
					onClick={handleToggleExpand}
					className={cn(
						'group',
						!canExpand && !detailPanel && 'opacity-30',
						buttonProps?.className,
						props.className
					)}
					style={{
						marginLeft:
							positionExpandColumn === 'last'
								? undefined
								: `${row.depth * 16}px`,
						marginRight:
							positionExpandColumn === 'last'
								? `${row.depth * 16}px`
								: undefined,
					}}
				>
					<ChevronRightIcon
						className='opacity-50 group-hover:opacity-100'
						style={{
							transform: `rotate(${
								!canExpand && !renderDetailPanel
									? positionExpandColumn === 'last'
										? 90
										: -90
									: isExpanded
										? 90
										: 0
							}deg)`,
							transition: 'transform 150ms',
						}}
					/>
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{buttonProps?.title ??
					(isExpanded ? localization.collapse : localization.expand)}
			</TooltipContent>
		</Tooltip>
	)
}
